import type { OMSConfig, StrategyEvaluator, SignalResult, StrategyContext } from '../types';
import { ensureFlatOpts } from '../../analysis/trend-evaluator';
import { extractSnapshotOptions } from '../../../lib/snapshot-search';

/**
 * HolyGrailDoubleConfirmation — High Conviction Trend Follower
 *
 * How it works:
 * - Requires two strictly independent mathematical models to agree.
 * - Buys CE ONLY when `Connecting Dots` is heavily bullish (≥ +4) AND the `Total Call Negative Count` indicates panic.
 * - Buys PE ONLY when `Connecting Dots` is heavily bearish (≤ -4) AND the `Total Put Negative Count` indicates panic.
 */
export const evaluateHolyGrailDoubleConfirmation: StrategyEvaluator = (data: any, currentUnixTime: number, config: OMSConfig, context: StrategyContext): SignalResult | null => {
    if (!data) return null;

    const { symbol, expiry, gap } = context;
    const spot = data.spotPrice || data.index_ltp || data.ltp || 0;
    if (spot <= 0) return null;

    const trendAnalysisResult = context.liveIndicators?.trendAnalysisData;
    const fullTrendData = Array.isArray(trendAnalysisResult) ? trendAnalysisResult : (trendAnalysisResult?.data || []);
    
    if (fullTrendData.length < 1) return null;

    const latestRow = fullTrendData[0];
    const dotsScore = latestRow?.dotsScore ?? 0;
    const callCount = latestRow?.totalCallCount ?? 0;
    const putCount = latestRow?.totalPutCount ?? 0;

    const panicThreshold = -10; // Highly negative implies panic unwinding

    let status: 'BULL' | 'BEAR' | 'Sideways' = 'Sideways';
    let remarks = '';

    // Double Confirmation: Dots >= +4 AND Call writers panicking
    if (dotsScore >= 4 && callCount <= panicThreshold) {
        status = 'BULL';
        remarks = `Double Confirmation: Dots (+${dotsScore}) & CE Panic (${callCount})`;
    }
    // Double Confirmation: Dots <= -4 AND Put writers panicking
    else if (dotsScore <= -4 && putCount <= panicThreshold) {
        status = 'BEAR';
        remarks = `Double Confirmation: Dots (${dotsScore}) & PE Panic (${putCount})`;
    }

    if (status === 'Sideways') return null;

    const liveRawOptsArr = context.chain || extractSnapshotOptions(data, symbol, expiry) || [];
    const liveFlatOpts = ensureFlatOpts(liveRawOptsArr);
    if (!liveFlatOpts.length) return null;

    const atmStrike = Math.round(spot / gap) * gap;
    const optionType = status === 'BULL' ? 'CE' : 'PE';

    let entryPrice = 0;
    const atmRow = liveFlatOpts.find((r: any) => r.strike === atmStrike && r.type === optionType);
    if (atmRow) {
        entryPrice = atmRow.ltp || atmRow.close || atmRow.price || 0;
    }
    
    if (entryPrice <= 0) return null;

    return {
        trade_type: status,
        strike: atmStrike,
        option_type: optionType,
        entry_price: entryPrice,
        remarks: remarks
    };
};
